import { useMemo } from 'react';
import useVersionManagementV1 from './useVersionManagementV1';
import { parseMarkdownIntoSections } from '../utils/markdownSectionParser';

/**
 * Custom hook to manage feature versions
 *
 * Uses useVersionManagementV1 which fixes:
 * 1. RHS edits not syncing to LHS (content change detection)
 * 2. Stable version IDs based on message index
 */
const useFeatureVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  // Configuration for feature version management
  const featureConfig = {
    // Determine if a message contains features
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      return content.includes('## F0') || content.includes('**Priority:**') || content.includes('**Complexity:**');
    },

    // Parse features from content — prefer backend-parsed JSON when available
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      return parseMarkdownIntoSections(content);
    },

    // Extract version information from a message
    // Note: The id will be set by useVersionManagement based on messageIndex
    // We return null for id here to let the parent hook set it properly
    extractVersions: (msg, parsedFeatures, versionNumber, isPreview = false) => {
      return {
        id: null, // Will be overwritten by useVersionManagement with messageIndex-based ID
        timestamp: msg.createdAt || new Date().toISOString(),
        features: parsedFeatures,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  };

  // Use the fixed version management hook (V1)
  const versionData = useVersionManagementV1(featureConfig);

  // Memoize the currentVersion to prevent unnecessary re-renders
  // Only recreate when the actual version data changes
  const currentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;
    return {
      ...versionData.currentVersion,
      features: versionData.currentVersion.parsedContent || versionData.currentVersion.features
    };
  }, [versionData.currentVersion]);

  // Map the generic version data to feature-specific structure
  return {
    versions: versionData.versions,
    currentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useFeatureVersions;